import clsx from "clsx";
import { forwardRef, useState } from "react";
import { getInputStyle } from "../helpers";
import { InputType, InputMode } from "../types";

type SelectedInputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

type Props = InputProps & SelectedInputProps;

type InputProps = {
  type: InputType;
  inputMode: InputMode;
  pattern: string;
  ariaLabel?: string;
  index: number;
  hasError?: boolean;
  onInputUpdate: () => void;
};
const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      onPaste,
      hasError = false,
      onInputUpdate,
      onChange,
      onFocus,
      onKeyDown,
      type,
      ariaLabel,
      index,
      disabled,
      placeholder,
      ...inputProps
    },
    ref,
  ) => {
    const [filled, setFilled] = useState<boolean>(false);
    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = e;
      const target = e.target as HTMLInputElement;
      if (onKeyDown) onKeyDown(e);
      if (key === "Backspace") {
        setFilled(false);
        if (target.value === "") {
          if (target.previousElementSibling !== null) {
            const t = target.previousElementSibling as HTMLInputElement;
            t.value = "";
            t.focus();
            e.preventDefault();
          }
        } else {
          target.value = "";
        }
        onInputUpdate();
      }
    };
    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
      if (onFocus) onFocus(e);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value, nextElementSibling },
      } = e;
      if (value.length > 1) {
        e.target.value = value.charAt(0);
        setFilled(true);
        if (nextElementSibling !== null) {
          (nextElementSibling as HTMLInputElement).focus();
        }
      } else {
        if (value.match(inputProps.pattern)) {
          setFilled(true);
          if (nextElementSibling !== null) {
            (nextElementSibling as HTMLInputElement).focus();
          }
        } else {
          setFilled(false);
          e.target.value = "";
        }
      }
      if (onChange) onChange(e);
      onInputUpdate();
    };

    return (
      <input
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onFocus={handleOnFocus}
        onPaste={onPaste}
        {...inputProps}
        type={type}
        ref={ref}
        maxLength={1}
        aria-label={
          ariaLabel
            ? `${ariaLabel} - Character ${index + 1}.`
            : `Character ${index + 1}.`
        }
        className={clsx(getInputStyle(hasError, filled, disabled), className)}
        disabled={disabled}
        placeholder={placeholder}
      />
    );
  },
);

Input.displayName = "MFA Input";

export default Input;
